import { useState, useEffect } from "react";
import { supabase } from "../../../../integrations/supabase/client";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";
import { Tabs, TabsContent/* , TabsList, TabsTrigger */ } from "../../../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import { useToast } from "../../../../hooks/use-toast";
import { Loader2, ArrowLeft } from "lucide-react";
import OutletManagement from "./settings/OutletManagement";

interface SettingProps {
  props: any;
  outletList:string[];
  supabaseoutletIDs?:string[]
  onRecallOulet: (recall: boolean) => void;
}

const Settings = ({ props, outletList, supabaseoutletIDs, onRecallOulet }: SettingProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  const [profile, setProfile] = useState<any>(null);
  const [epicBearerToken, setEpicBearerToken] = useState(localStorage.getItem('epic_bearer_token') || '');
  const [formData, setFormData] = useState({
    businessName: "",
    googleMapsUrl: "",
    instagramUsername: "",
    tiktokUsername: "",
    facebookUrl: "",
    tripadvisorUrl: "",
    reviewLimit: 100,
    googleScraperVersion: "google_places" as "google_places" | "google_reviews_v2",
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const { data/* , error */ } = await supabase
        .from("business_profile")
        .select("*")
        .maybeSingle();

      if (!data) {
        return;
      }

      setProfile(data);
      setFormData({
        businessName: data.business_name || "",
        googleMapsUrl: data.google_maps_url || "",
        instagramUsername: data.instagram_username || "",
        tiktokUsername: data.tiktok_username || "",
        facebookUrl: data.facebook_url || "",
        tripadvisorUrl: data.tripadvisor_url || "",
        reviewLimit: data.review_limit || 100,
        googleScraperVersion: (data.google_scraper_version || "google_places") as "google_places" | "google_reviews_v2",
      });
    } catch (error) {
      console.error("Error loading profile:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from("business_profile")
        .update({
          business_name: formData.businessName,
          google_maps_url: formData.googleMapsUrl || null,
          instagram_username: formData.instagramUsername || null,
          tiktok_username: formData.tiktokUsername || null,
          facebook_url: formData.facebookUrl || null,
          tripadvisor_url: formData.tripadvisorUrl || null,
          review_limit: formData.reviewLimit,
          google_scraper_version: formData.googleScraperVersion,
        })
        .eq("id", profile.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Business profile updated successfully!",
      });
      props.navigate('/clientpanel/rating');
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update business profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto">
        {supabaseoutletIDs.length>0 &&
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => props.navigate('/clientpanel/rating')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        }

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Settings</h1>
          <p className="text-muted-foreground">
            Update your business information and platform connections
          </p>
        </div>

        <Tabs defaultValue="outlets" className="space-y-6">
         {/*  <TabsList>
            <TabsTrigger value="business">Business Info</TabsTrigger>
            <TabsTrigger value="outlets">Outlet Management</TabsTrigger>
            <TabsTrigger value="epicreviews">EpicReviews API</TabsTrigger>
          </TabsList> */}

          <TabsContent value="business">
            <Card>
              <CardHeader>
                <CardTitle>Business Information</CardTitle>
                <CardDescription>
                  Configure global business settings. Platform URLs have been moved to Outlet Management for multi-location control.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="businessName">Business Name *</Label>
                    <Input
                      id="businessName"
                      placeholder="My Business"
                      value={formData.businessName}
                      onChange={(e) =>
                        setFormData({ ...formData, businessName: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="p-4 bg-muted rounded-lg space-y-2">
                    <p className="text-sm font-medium">💡 Platform URLs Moved</p>
                    <p className="text-sm text-muted-foreground">
                      Platform scraping URLs are now managed per outlet in the "Outlet Management" tab. 
                      This allows you to scrape reviews for multiple locations independently.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Legacy URLs below are maintained for backward compatibility.
                    </p>
                  </div>

                  <div className="space-y-2 opacity-60">
                    <Label htmlFor="googleMapsUrl">Google Maps Business URL (Legacy)</Label>
                    <Input
                      id="googleMapsUrl"
                      placeholder="https://maps.google.com/..."
                      value={formData.googleMapsUrl}
                      onChange={(e) =>
                        setFormData({ ...formData, googleMapsUrl: e.target.value })
                      }
                      disabled
                    />
                  </div>

                  <div className="space-y-2 opacity-60">
                    <Label htmlFor="instagramUsername">Instagram Username (Legacy)</Label>
                    <Input
                      id="instagramUsername"
                      placeholder="@username"
                      value={formData.instagramUsername}
                      onChange={(e) =>
                        setFormData({ ...formData, instagramUsername: e.target.value })
                      }
                      disabled
                    />
                  </div>

                  <div className="space-y-2 opacity-60">
                    <Label htmlFor="tiktokUsername">TikTok Username (Legacy)</Label>
                    <Input
                      id="tiktokUsername"
                      placeholder="@username"
                      value={formData.tiktokUsername}
                      onChange={(e) =>
                        setFormData({ ...formData, tiktokUsername: e.target.value })
                      }
                      disabled
                    />
                  </div>

                  <div className="space-y-2 opacity-60">
                    <Label htmlFor="facebookUrl">Facebook Page URL (Legacy)</Label>
                    <Input
                      id="facebookUrl"
                      placeholder="https://facebook.com/..."
                      value={formData.facebookUrl}
                      onChange={(e) =>
                        setFormData({ ...formData, facebookUrl: e.target.value })
                      }
                      disabled
                    />
                  </div>

                  <div className="space-y-2 opacity-60">
                    <Label htmlFor="tripadvisorUrl">Tripadvisor Business URL (Legacy)</Label>
                    <Input
                      id="tripadvisorUrl"
                      placeholder="https://tripadvisor.com/..."
                      value={formData.tripadvisorUrl}
                      onChange={(e) =>
                        setFormData({ ...formData, tripadvisorUrl: e.target.value })
                      }
                      disabled
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reviewLimit">Review Fetch Limit</Label>
                    <Input
                      id="reviewLimit"
                      type="number"
                      min="10"
                      max="1000"
                      placeholder="100"
                      value={formData.reviewLimit}
                      onChange={(e) =>
                        setFormData({ ...formData, reviewLimit: parseInt(e.target.value) || 100 })
                      }
                    />
                    <p className="text-sm text-muted-foreground">
                      Maximum number of latest reviews to fetch from each platform (10-1000)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="googleScraperVersion">Google Scraper Version</Label>
                    <Select
                      value={formData.googleScraperVersion}
                      onValueChange={(value: "google_places" | "google_reviews_v2") =>
                        setFormData({ ...formData, googleScraperVersion: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="google_places">Legacy (Places Crawler)</SelectItem>
                        <SelectItem value="google_reviews_v2">New (Reviews Scraper v2)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">
                      Choose which Google Maps scraper to use. The new version is 3-5× faster with cleaner data output.
                    </p>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="outlets">
            <OutletManagement
              outletList={outletList}
              onRecallOulet={(recall) => {
                onRecallOulet(recall);
              }}
              supabaseoutletIDs={supabaseoutletIDs}
            />
          </TabsContent>

          <TabsContent value="epicreviews">
            <Card>
              <CardHeader>
                <CardTitle>EpicReviews API Settings</CardTitle>
                <CardDescription>
                  Configure access for fetching EpicPay low-star review data (1-3 stars only).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="epicBearerToken">Bearer Token</Label>
                  <Input
                    id="epicBearerToken"
                    type="password"
                    placeholder="Enter your EpicReviews API token"
                    value={epicBearerToken}
                    onChange={(e) => {
                      setEpicBearerToken(e.target.value);
                      localStorage.setItem('epic_bearer_token', e.target.value);
                    }}
                  />
                  <p className="text-sm text-muted-foreground">
                    Token is stored locally in your browser (not in database)
                  </p>
                </div>
                
                <Button
                  onClick={async () => {
                    if (!epicBearerToken) {
                      toast({
                        title: "Error",
                        description: "Please enter a bearer token first",
                        variant: "destructive",
                      });
                      return;
                    }
                    
                    try {
                      const response = await fetch('https://walletapi.epicpay.ai/api/rating/list?limit=1&offset=1', {
                        headers: { 'Authorization': `Bearer ${epicBearerToken}` }
                      });
                      
                      if (response.ok) {
                        toast({
                          title: "Success",
                          description: "✅ EpicReviews API connection successful!",
                        });
                      } else {
                        throw new Error('Invalid token or API error');
                      }
                    } catch (error) {
                      toast({
                        title: "Error",
                        description: "❌ Failed to connect. Please check your token.",
                        variant: "destructive",
                      });
                    }
                  }}
                >
                  Test Connection
                </Button>

                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <p className="text-sm font-medium">💡 How to use:</p>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Enter your EpicPay API bearer token above</li>
                    <li>Click "Test Connection" to verify</li>
                    <li>Go to Outlet Management and add your EpicPay Outlet ID</li>
                    <li>Click "Fetch EpicReviews" to pull 1-3 star reviews</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;
